import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { portalLogin, getCurrentPortalUser } from "@/lib/portalApi";

export default function PortalLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const u = getCurrentPortalUser();
    if (u) {
      nav(redirectForRole(u.role), { replace: true });
    }
  }, [nav]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const res = await portalLogin(email.trim(), password);
      toast.success(`Welcome, ${res.user.name || res.user.email}`);
      nav(redirectForRole(res.user.role), { replace: true });
    } catch (err) {
      // Surface the most specific message we can — makes triage tractable
      // when a user pings us with just a red toast. Order of preference:
      //   1) Backend's structured detail (401/403 etc.)
      //   2) Backend HTTP status alone (500/502/... with no body)
      //   3) Axios network error (no response reached the browser)
      //   4) Generic fallback
      let msg;
      if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err?.response?.status) {
        msg = `Login failed — server returned HTTP ${err.response.status}. Please retry or contact support.`;
      } else if (err?.request) {
        msg = "Login failed — couldn't reach the server. Check your connection and try again.";
      } else {
        msg = err?.message || "Login failed";
      }
      toast.error(msg, { duration: 7000 });
      // Also log the raw error to the console so an admin looking over the
      // user's shoulder in DevTools sees the exact status + payload.
      if (typeof window !== "undefined" && window.console) {
        // eslint-disable-next-line no-console
        console.warn("[portal-login] failed:", {
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <img src="/logo.png" alt="AdmissionCrafts" className="h-11 w-11 object-contain select-none" draggable={false} />
          <div>
            <div className="font-display font-bold text-xl">Admission<span className="text-amber-400">Crafts</span></div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-300">Internal portal</div>
          </div>
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight">Sign in</h1>
        <p className="text-sm text-slate-400 mt-2">For Admin, Analyst &amp; approved Partner accounts.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="portal-login-form">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-slate-400">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-11 bg-slate-800 border-slate-700 text-white"
              placeholder="you@dhruva.test"
              data-testid="portal-email-input"
              required
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-slate-400">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 h-11 bg-slate-800 border-slate-700 text-white"
              placeholder="Your password"
              data-testid="portal-password-input"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full h-11 bg-amber-400 hover:bg-amber-500 text-amber-950 font-semibold rounded-lg"
            data-testid="portal-login-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-center text-xs text-slate-400 mt-3">
            <Link to="/portal/forgot-password" className="text-amber-300 hover:text-amber-200 underline" data-testid="portal-forgot-password">
              Forgot password?
            </Link>
          </p>
        </form>

        <div className="mt-8 text-center text-xs text-slate-400 space-y-2">
          <p>
            Want to become a partner?{" "}
            <Link to="/partners" className="text-amber-400 underline" data-testid="portal-to-apply">
              Apply here <ArrowRight className="w-3 h-3 inline" />
            </Link>
          </p>
          <p>
            <Link to="/" className="text-slate-300 hover:text-white underline" data-testid="portal-to-home">
              ← Back to student app
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function redirectForRole(role) {
  if (role === "admin") return "/portal/admin";
  if (role === "analyst") return "/portal/analyst";
  if (role === "partner") return "/portal/partner";
  return "/";
}
